import { ArrowRight, Megaphone, TrendingUp, ShoppingCart, Headphones } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";

const agentMeta = [
  {
    icon: Megaphone,
    iconColor: "text-chathero-mint",
    iconBg: "bg-chathero-mint/20",
    iconBorder: "border-chathero-mint/50",
    linkColor: "text-chathero-mint",
    link: "/loesungen/marketing",
  },
  {
    icon: TrendingUp,
    iconColor: "text-chathero-orange",
    iconBg: "bg-chathero-orange/20",
    iconBorder: "border-chathero-orange/50",
    linkColor: "text-chathero-orange",
    link: "/loesungen/vertrieb",
  },
  {
    icon: ShoppingCart,
    iconColor: "text-chathero-orange",
    iconBg: "bg-chathero-orange/20",
    iconBorder: "border-chathero-orange/50",
    linkColor: "text-chathero-orange",
    link: "/loesungen/ecommerce",
  },
  {
    icon: Headphones,
    iconColor: "text-chathero-mint",
    iconBg: "bg-chathero-mint/20",
    iconBorder: "border-chathero-mint/50",
    linkColor: "text-chathero-mint",
    link: "/loesungen/kundenservice",
  },
];

const AIAgentsRollenSection = () => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tObject, localizePath } = useLanguage();
  const agents = tObject<Array<{ name: string; description: string; linkText: string }>>('aiAgentPlattform.rollen.agents');

  return (
    <section 
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-background transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">{t('aiAgentPlattform.rollen.badge')}</Badge>
            
            <h2 className="text-3xl md:text-4xl font-semibold mb-2">
              <span className="text-foreground">{t('aiAgentPlattform.rollen.titleLine1')}</span>
              <br />
              <span className="text-foreground">{t('aiAgentPlattform.rollen.titleLine2')}</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {agents.map((agent, index) => {
              const meta = agentMeta[index];
              return (
                <div
                  key={index}
                  className={cn(
                    "bg-white rounded-2xl p-6 border border-border shadow-[0_4px_24px_rgba(0,196,180,0.08)] hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,196,180,0.14)] transition-all duration-300 group",
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                  )}
                  style={{ transitionDelay: isVisible ? `${index * 150}ms` : "0ms", transitionDuration: "500ms" }}
                >
                  <div className="flex items-start gap-5">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 ${meta.iconBg} border-2 ${meta.iconBorder} group-hover:scale-110 transition-transform duration-300`}>
                      <meta.icon className={`w-8 h-8 ${meta.iconColor}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-primary mb-2">{agent.name}</h3>
                      <p className="text-muted-foreground leading-relaxed mb-3">{agent.description}</p>
                      <Link 
                        to={localizePath(meta.link)}
                        className={`inline-flex items-center gap-2 text-sm ${meta.linkColor} font-medium hover:gap-3 transition-all`}
                      >
                        {agent.linkText}
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div 
            className={cn(
              "bg-chathero-mint/10 rounded-2xl p-6 border border-chathero-mint/20 text-center mt-10 transition-all duration-500",
              isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
            )}
            style={{ transitionDelay: isVisible ? "600ms" : "0ms" }}
          >
            <p className="text-lg font-semibold text-primary">
              {t('aiAgentPlattform.rollen.highlight')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIAgentsRollenSection;
